import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Row, Col, Pagination, Empty, Spin } from 'antd';
import { LoadingOutlined, StopOutlined } from '@ant-design/icons';
import CustomNavbar from '../components/Navbar';
import BlockedProfileCard from '../components/BlockedProfileCard';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';
import { notification } from 'antd';

const BlockedProfiles = () => {
    const { profileId } = useProfile();
    const [navbarReady, setNavbarReady] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, total: 0 });
    const [pageSize] = useState(12);

    const fetchBlockedProfiles = useCallback(async (page = 1) => {
        if (!profileId) return;
        setLoading(true);
        try {
            const res = await api.post(`/blocked-profiles/list?page=${page}&limit=${pageSize}`, {
                profile_id: profileId,
            });
            if (res.data.status === 'success') {
                setProfiles(res.data.data.data);
                setPagination({ current: page, total: res.data.data.total });
            }
        } catch {
            notification.error({ message: 'Failed to load blocked profiles.' });
        } finally {
            setLoading(false);
        }
    }, [profileId, pageSize]);

    useEffect(() => {
        if (navbarReady && profileId) fetchBlockedProfiles(1);
    }, [navbarReady, profileId, fetchBlockedProfiles]);

    const handleUnblock = async (blockId) => {
        try {
            const res = await api.delete(`/blocked-profiles/unblock/${blockId}`, {
                data: { blocker_id: profileId },
            });
            if (res.data.status === 'success') {
                notification.success({ message: 'Profile unblocked successfully.' });
                setProfiles(prev => prev.filter(p => p.id !== blockId));
                setPagination(prev => ({ ...prev, total: prev.total - 1 }));
            }
        } catch {
            notification.error({ message: 'Failed to unblock profile.' });
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
                </div>
            );
        }

        if (!profiles.length) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<span style={{ color: '#94A3B8' }}>You haven't blocked any profiles</span>}
                    />
                </div>
            );
        }

        return (
            <>
                <Row gutter={[16, 16]}>
                    {profiles.map(profile => (
                        <Col key={profile.id} xs={24} sm={12} md={8} lg={6}>
                            <BlockedProfileCard profile={profile} onUnblock={handleUnblock} />
                        </Col>
                    ))}
                </Row>
                {pagination.total > pageSize && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
                        <Pagination
                            current={pagination.current}
                            total={pagination.total}
                            pageSize={pageSize}
                            onChange={fetchBlockedProfiles}
                            showSizeChanger={false}
                        />
                    </div>
                )}
            </>
        );
    };

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Helmet><title>Blocked Profiles | Oottan</title></Helmet>
            <CustomNavbar onNavbarReady={() => setNavbarReady(true)} />

            <div style={{ marginTop: '64px', padding: '24px', flex: 1, overflowY: 'auto' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                        <StopOutlined style={{ fontSize: '20px', color: '#EF4444' }} />
                        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1A1A2E' }}>
                            Blocked Profiles
                        </h2>
                        {pagination.total > 0 && (
                            <span style={{
                                background: 'rgba(239,68,68,0.1)', color: '#EF4444',
                                borderRadius: '20px', padding: '2px 10px', fontSize: '0.82rem', fontWeight: 600,
                            }}>
                                {pagination.total}
                            </span>
                        )}
                    </div>

                    {navbarReady ? renderContent() : (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BlockedProfiles;
